/**
 * Client-side telemetry — Zero query metrics, navigations, long tasks, errors.
 *
 * Creates real OTel spans via otel.ts, so everything flows through
 * a single pipeline (browser → /telemetry/traces → collector → Honeycomb)
 * and naturally participates in distributed traces.
 *
 * Gated on the API's CLIENT_TRACING_SECRET — if the secret is unset on the
 * server, the token endpoint returns 404 and nothing is collected.
 */

import type { Zero } from "@rocicorp/zero";
import { telemetryTokenPath } from "./apiPaths";

// ------------------------------------------------------------------
// State
// ------------------------------------------------------------------

type EmitSpan = (
  name: string,
  attrs: Record<string, string | number>,
  durationMs?: number,
  endTimeMs?: number
) => void;

type PendingSpan = {
  name: string;
  attrs: Record<string, string | number>;
  durationMs?: number;
  endTimeMs: number;
};

export function createSpanEmitter(maxPendingSpans = 100) {
  let emitSpan: EmitSpan | undefined;
  let buffering = true;
  const pending: PendingSpan[] = [];

  return {
    emit(span: PendingSpan) {
      if (emitSpan) {
        emitSpan(span.name, span.attrs, span.durationMs, span.endTimeMs);
      } else if (buffering && pending.length < maxPendingSpans) {
        pending.push(span);
      }
    },
    enable(emit: EmitSpan, beforeFlush: () => void) {
      beforeFlush();
      emitSpan = emit;
      buffering = false;
      for (const span of pending) {
        emit(span.name, span.attrs, span.durationMs, span.endTimeMs);
      }
      pending.length = 0;
    },
    disable() {
      buffering = false;
      pending.length = 0;
    },
    isCollecting() {
      return (
        emitSpan !== undefined ||
        (buffering && pending.length < maxPendingSpans)
      );
    },
  };
}

export const sessionId = Math.random().toString(36).slice(2, 8);
let initialized = false;
const spanEmitter = createSpanEmitter();
let resetPageTraceFn:
  | ((attrs?: Record<string, string | number>, startTimeMs?: number) => void)
  | undefined;

// ------------------------------------------------------------------
// Span helpers
// ------------------------------------------------------------------

export const emitSpan: EmitSpan = (name, attrs, durationMs, endTimeMs) => {
  const span = {
    name,
    attrs: {
      "session.id": sessionId,
      "page.path": location.pathname,
      ...attrs,
    },
    durationMs,
    endTimeMs: endTimeMs ?? performance.timeOrigin + performance.now(),
  };
  spanEmitter.emit(span);
};

function errorAttributes(
  prefix: string,
  error: unknown
): Record<string, string> {
  if (error instanceof Error) {
    return {
      [`${prefix}.message`]: error.message.slice(0, 200),
      [`${prefix}.type`]: error.name,
    };
  }
  if (error == null || typeof error !== "object") {
    return { [`${prefix}.message`]: String(error).slice(0, 200) };
  }

  const attrs: Record<string, string> = {};
  if ("type" in error && typeof error.type === "string") {
    attrs[`${prefix}.type`] = error.type;
  }
  if ("kind" in error && typeof error.kind === "string") {
    attrs[`${prefix}.kind`] = error.kind;
  }
  if ("message" in error && typeof error.message === "string") {
    attrs[`${prefix}.message`] = error.message.slice(0, 200);
    return attrs;
  }

  attrs[`${prefix}.message`] = JSON.stringify(error).slice(0, 200);
  return attrs;
}

function errorAttributesWithLegacyMessage(
  prefix: string,
  legacyMessageKey: string,
  error: unknown
): Record<string, string> {
  const attrs = errorAttributes(prefix, error);
  return {
    ...attrs,
    [legacyMessageKey]: attrs[`${prefix}.message`] ?? "",
  };
}

// ------------------------------------------------------------------
// Public check
// ------------------------------------------------------------------

export function isTelemetryActive() {
  return initialized;
}

export function isTelemetryCollecting() {
  return spanEmitter.isCollecting();
}

// ------------------------------------------------------------------
// Query tracking (called from useQuery wrapper)
// ------------------------------------------------------------------

export function trackQueryComplete({
  name,
  durationMs,
  rows,
  bytes,
  cached,
  firstDataMs,
  duringNavigation,
  deferred,
  navigationStartOffsetMs,
}: {
  name: string;
  durationMs: number;
  rows: number;
  bytes: number;
  cached: boolean;
  firstDataMs: number | undefined;
  duringNavigation: boolean;
  deferred: boolean;
  // Activation offset into the navigation; undefined if none was in flight.
  navigationStartOffsetMs: number | undefined;
}) {
  emitSpan(
    "ui.query",
    {
      "zero.query.name": name,
      "zero.query.event": "complete",
      "zero.query.duration_ms": Math.round(durationMs),
      "zero.query.rows": rows,
      "zero.query.bytes": bytes,
      "zero.query.cached": cached ? 1 : 0,
      "zero.query.navigation": duringNavigation ? 1 : 0,
      "zero.query.deferred": deferred ? 1 : 0,
      ...(navigationStartOffsetMs !== undefined
        ? {
            "zero.query.navigation_start_offset_ms": Math.round(
              navigationStartOffsetMs
            ),
          }
        : {}),
      ...(firstDataMs !== undefined
        ? {
            "zero.query.first_data_ms": Math.round(firstDataMs),
            "zero.query.flush_gap_ms": Math.round(
              Math.max(0, durationMs - firstDataMs)
            ),
          }
        : {}),
    },
    durationMs
  );
}

function resetPageTrace(startTimeMs?: number) {
  resetPageTraceFn?.({ "session.id": sessionId }, startTimeMs);
}

// ------------------------------------------------------------------
// Mutation tracking (called from ZeroSetup's mutate patch)
// ------------------------------------------------------------------

export function trackMutation(
  name: string,
  start: number,
  phase: "client" | "server",
  result: { type: string; error?: unknown }
) {
  const durationMs = performance.now() - start;
  emitSpan(
    "ui.mutation",
    {
      "zero.mutation.name": name,
      "zero.mutation.phase": phase,
      "zero.mutation.result": result.type,
      "zero.mutation.duration_ms": Math.round(durationMs),
      ...(result.type === "error" && result.error
        ? errorAttributesWithLegacyMessage(
            "zero.mutation.error",
            "zero.mutation.error",
            result.error
          )
        : {}),
    },
    durationMs
  );
}

// ------------------------------------------------------------------
// One-off UI interaction events
// ------------------------------------------------------------------

export function trackUiEvent(
  name: string,
  attrs: Record<string, string | number> = {}
) {
  emitSpan(name, attrs);
}

// Video tracking (called from video player elements)
// ------------------------------------------------------------------

export function trackVideoEvent(
  name: string,
  event: "play" | "pause" | "ended"
) {
  emitSpan("ui.video", {
    "ui.video.name": name,
    "ui.video.event": event,
  });
}

// ------------------------------------------------------------------
// Connection state tracking
// ------------------------------------------------------------------

/** Subscribe to Zero connection state changes — disconnects, reconnects, errors. */
export function trackConnectionState(z: Zero) {
  let connectStart = 0;

  z.connection.state.subscribe((state) => {
    if (state.name === "connecting") {
      connectStart = performance.now();
      return;
    }

    if (state.name === "connected" && connectStart > 0) {
      const duration = performance.now() - connectStart;
      connectStart = 0;
      emitSpan(
        "ui.zero.connect",
        {
          "zero.connect.duration_ms": Math.round(duration),
        },
        duration
      );
      return;
    }

    if (
      state.name === "disconnected" ||
      state.name === "error" ||
      state.name === "needs-auth"
    ) {
      const reason = "reason" in state ? state.reason : undefined;
      emitSpan("ui.zero.connection", {
        "zero.connection.state": state.name,
        ...(reason
          ? errorAttributesWithLegacyMessage(
              "zero.connection.reason",
              "zero.connection.reason",
              reason
            )
          : {}),
      });
    }
  });
}

// ------------------------------------------------------------------
// Long task observer
// ------------------------------------------------------------------

export function trackLongTask(
  entry: Pick<PerformanceEntry, "duration" | "startTime">
) {
  emitSpan(
    "ui.longtask",
    { "ui.longtask.duration_ms": Math.round(entry.duration) },
    entry.duration,
    performance.timeOrigin + entry.startTime + entry.duration
  );
}

function startLongTaskObserver() {
  if (typeof PerformanceObserver === "undefined") return;
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        trackLongTask(entry);
      }
    });
    observer.observe({ type: "longtask", buffered: true });
  } catch {
    // longtask not supported in this browser
  }
}

// ------------------------------------------------------------------
// Error tracking
// ------------------------------------------------------------------

function startErrorTracking() {
  addEventListener("error", (event: ErrorEvent) => {
    emitSpan("ui.error", {
      ...errorAttributes("ui.error", event.error ?? event.message ?? "unknown"),
      "ui.error.source": "unhandled",
    });
  });

  addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    emitSpan("ui.error", {
      ...errorAttributes("ui.error", event.reason ?? "unknown"),
      "ui.error.source": "unhandled-rejection",
    });
  });
}

// ------------------------------------------------------------------
// Init
// ------------------------------------------------------------------

/** Wire span emission and start the first page trace. */
export function enableTracing(
  emit: EmitSpan,
  resetTrace?: (
    attrs?: Record<string, string | number>,
    startTimeMs?: number
  ) => void
) {
  resetPageTraceFn = resetTrace;
  initialized = true;
  spanEmitter.enable(emit, () => resetPageTrace(performance.timeOrigin));
}

function markTracingUnavailable() {
  spanEmitter.disable();
}

let initRequested = false;

export function initTracing(externalApiUrl: string, tenantId: string) {
  if (initRequested) return;
  initRequested = true;

  // Lazy imports to avoid pulling in environment.ts (which accesses document)
  // at module load time — that breaks Node-based test files that transitively
  // import this module via useQuery.
  Promise.all([import("../environment"), import("./otel")])
    .then(([{ config }, bt]) => {
      if (!config.tracingEnabled) {
        markTracingUnavailable();
        return;
      }
      return fetch(`${externalApiUrl}${telemetryTokenPath()}`, {
        credentials: "include",
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { token: string; tracesEnabled?: boolean } | null) => {
          if (!data?.token || !data.tracesEnabled) {
            markTracingUnavailable();
            return;
          }

          bt.initBrowserTracing(data.token, externalApiUrl, tenantId);
          enableTracing(bt.emitSpan, bt.resetPageTrace);

          addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") bt.flushTraces();
          });

          addEventListener("popstate", () => resetPageTrace());

          startLongTaskObserver();
          startErrorTracking();
        });
    })
    .catch(markTracingUnavailable);
}
